import CloseIcon from "@mui/icons-material/Close";
import type { RefObject } from "react";
import { TOUR_COPY, TOUR_GEOMETRY } from "@/constants/tour";
import type { CardPosition, TourStep } from "@/interfaces/tour";

const CLOSE_ICON_SIZE_PX = 19;

/**
 * Shared button geometry, carrying NO colour utilities — each variant supplies its own. Competing
 * Tailwind utilities have equal specificity, so appending `bg-rodeo-black` onto a base holding
 * `bg-white` is decided by stylesheet order, not class order (it lost: white text on white).
 */
const BUTTON_CLASS =
  "cursor-pointer rounded-[8px] border px-4 py-[9px] font-bold font-lato text-[13px] transition-colors";
const SECONDARY_BUTTON_CLASS = `${BUTTON_CLASS} border-smoke bg-white text-rodeo-black hover:border-dark-dust`;
const PRIMARY_BUTTON_CLASS = `${BUTTON_CLASS} border-rodeo-black bg-rodeo-black text-white hover:border-rodeo-black hover:bg-background-dark`;
const DOT_CLASS = "h-1.5 transition-[width,background-color] duration-200";

/**
 * `transform` is deliberately NOT in the transition list: it carries the centring offset, and a
 * card that animated into the centre would be sliding through coordinates that mean nothing.
 * Tailwind v4 emits `translate` (not `transform`) for `translate-y-*`, so the two never collide.
 */
const CARD_CLASS =
  "fixed z-1 max-w-[calc(100vw-32px)] rounded-[14px] border border-smoke bg-white p-5 pb-4 shadow-[0_18px_44px_color-mix(in_srgb,var(--color-rodeo-black)_28%,transparent)] outline-none transition-opacity duration-[450ms] ease-[cubic-bezier(.22,1,.36,1)]";
const CARD_VISIBLE_CLASS = "translate-y-0 opacity-100 pointer-events-auto";
const CARD_HIDDEN_CLASS = "translate-y-[10px] opacity-0 pointer-events-none";

/**
 * Centred by CSS alone — no measured height, so a step with no coordinates needs no measurement.
 *
 * This is also the fallback for an unmeasured card, in place of `0`: only `opacity` is in the
 * transition list above, so `top`/`left`/`transform` are applied on the frame they change while
 * the fade is barely under way. A fallback of 0/0 therefore does not hide the card — it paints it,
 * still nearly opaque, in the corner of the viewport, which is what the transition between two
 * steps looked like (GFG-191).
 */
const CENTERED_OFFSET = "50%";
const CENTERED_TRANSFORM = "translate(-50%, -50%)";

interface TourCardProps {
  step: TourStep;
  /** 0-based index of `step` within the tour. */
  index: number;
  total: number;
  /**
   * Measured coordinates once the step's targets are placed, `"center"` while it settles, or null
   * before either — then the card is hidden but still laid out, so its height can be read. Both
   * non-measured modes render at the same place: the centre. Only their opacity differs.
   */
  position: CardPosition | "center" | null;
  /** Focus-trap container (SpotlightTour also measures this node). */
  containerRef: RefObject<HTMLDivElement>;
  onBack: () => void;
  onNext: () => void;
  onEnd: () => void;
}

/** Presentational: every string comes from `TOUR_COPY`, every position from the overlay. */
export default function TourCard({
  step,
  index,
  total,
  position,
  containerRef,
  onBack,
  onNext,
  onEnd,
}: TourCardProps) {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  // Pixels only when this step has been measured. Everything else — settling, and the commits
  // between two measurements — is centred, so the card is never painted at coordinates that do
  // not mean anything.
  const coords: CardPosition | null = position === "center" ? null : position;
  const isCentered = coords === null;

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={`${CARD_CLASS} ${position === null ? CARD_HIDDEN_CLASS : CARD_VISIBLE_CLASS}`}
      style={{
        top: coords?.top ?? CENTERED_OFFSET,
        left: coords?.left ?? CENTERED_OFFSET,
        width: TOUR_GEOMETRY.cardWidth,
        transform: isCentered ? CENTERED_TRANSFORM : undefined,
      }}
    >
      <button
        type="button"
        onClick={onEnd}
        aria-label={TOUR_COPY.closeAria}
        className="absolute top-3 right-3 flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-graphite transition-colors hover:bg-smoke hover:text-rodeo-black"
      >
        <CloseIcon sx={{ fontSize: CLOSE_ICON_SIZE_PX }} />
      </button>

      <div className="mb-1.5 font-bold font-lato text-[11px] text-dark-dust uppercase tracking-[1.5px]">
        {step.badge}
      </div>
      <h3 className="mb-2 pr-[22px] font-crimson font-normal text-[22px] text-rodeo-black leading-[1.2]">
        {step.title}
      </h3>
      <p className="mb-[18px] font-lato text-graphite text-sm leading-[1.55]">{step.body}</p>

      <div className="flex items-center justify-between gap-2.5">
        {/* The last step drops Skip; the spacer keeps the nav buttons right-aligned. */}
        {isLast ? (
          <span />
        ) : (
          <button
            type="button"
            onClick={onEnd}
            className="cursor-pointer border-none bg-transparent px-1 py-2 font-bold font-lato text-[13px] text-graphite transition-colors hover:text-rodeo-black hover:underline"
          >
            {TOUR_COPY.skipTour}
          </button>
        )}
        <div className="flex gap-2">
          {/* Kept in the layout on step 1 (invisible, inert) so the row never jumps. */}
          <button
            type="button"
            onClick={onBack}
            disabled={isFirst}
            className={
              isFirst
                ? `${SECONDARY_BUTTON_CLASS} pointer-events-none opacity-0`
                : SECONDARY_BUTTON_CLASS
            }
          >
            {TOUR_COPY.back}
          </button>
          <button type="button" onClick={isLast ? onEnd : onNext} className={PRIMARY_BUTTON_CLASS}>
            {isLast ? TOUR_COPY.endTour : TOUR_COPY.next}
          </button>
        </div>
      </div>

      <div aria-hidden="true" className="mt-3.5 flex justify-center gap-1.5">
        {Array.from({ length: total }, (_, dotIndex) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: positional dots, never reordered
            key={dotIndex}
            className={
              dotIndex === index
                ? `${DOT_CLASS} w-[18px] rounded-[3px] bg-dark-dust`
                : `${DOT_CLASS} w-1.5 rounded-full bg-smoke`
            }
          />
        ))}
      </div>
    </div>
  );
}
